import React from "react";
import { useTranslation } from "react-i18next";
import { EventResponse, PostEvent } from "types/Post";
import { useEventAnswer } from "../hooks/useEventAnswer";
import { formatWeekdayAndTime, useUiLanguage } from "../utils/newsDates";
import { EventDateBadge } from "./EventDateBadge";

const RESPONSE_LABEL_KEYS: Record<EventResponse, string> = {
  going: "news_event_going",
  not_going: "news_event_not_going",
};

interface EventSectionProps {
  event: PostEvent;
  /** `null` takes the viewer's answer back. */
  onRespond: (response: EventResponse | null) => Promise<void>;
}

/**
 * The event of a post: date tile, title, when and where, and the viewer's answer. Pressing the
 * chosen answer again takes it back. Once the event has started nobody answers any more.
 */
export function EventSection({ event, onRespond }: EventSectionProps) {
  const { t } = useTranslation();
  const language = useUiLanguage();
  const { isSaving, answer } = useEventAnswer(onRespond);
  const hasStarted = event.startsAt < Date.now();
  const whenAndWhere = [formatWeekdayAndTime(event.startsAt, language), event.location].filter(Boolean).join(" · ");

  return (
    <div className="mb-3 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3" data-testid="post-event">
      <EventDateBadge startsAt={event.startsAt} />
      <div className="min-w-0 flex-1">
        <p className="break-words font-semibold text-gray-900">{event.title}</p>
        <p className="text-sm text-gray-600">{whenAndWhere}</p>
        {hasStarted ? (
          <p className="mt-2 text-sm font-medium text-gray-500">{t("news_event_past")}</p>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {(Object.keys(RESPONSE_LABEL_KEYS) as EventResponse[]).map((response) => {
              const isChosen = event.myResponse === response;
              return (
                <button
                  key={response}
                  type="button"
                  aria-pressed={isChosen}
                  disabled={isSaving}
                  onClick={() => answer(isChosen ? null : response)}
                  className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                    isChosen
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {t(RESPONSE_LABEL_KEYS[response])}
                </button>
              );
            })}
            <span className="text-sm text-gray-600">{t("news_event_going_count", { count: event.goingCount })}</span>
          </div>
        )}
      </div>
    </div>
  );
}
